import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Mail, ExternalLink, User } from 'lucide-react';
import { getPersonById } from '../data/peopleData';
import MichaelHeneiseProfile from './MichaelHeneiseProfile';
import LanuakumAierProfile from './LanuakumAierProfile';
import CatrionaChildProfile from './CatrionaChildProfile';
import RuokuonuoYhomeProfile from './RuokuonuoYhomeProfile';
import RovithonoYhomeProfile from './RovithonoYhomeProfile';
import AkumtongImchenProfile from './AkumtongImchenProfile';
import SaktumWontiProfile from './SaktumWontiProfile';
import ShoulMoviProfile from './ShoulMoviProfile';
import KhrieletuonuoYhomeProfile from './KhrieletuonuoYhomeProfile';
import TumuzoKatiryProfile from './TumuzoKatiryProfile';
import EstherSwuProfile from './EstherSwuProfile';
import KevideLchoProfile from './KevideLchoProfile';
import AkumjungPongenProfile from './AkumjungPongenProfile';
import MargaretLyngdohProfile from './MargaretLyngdohProfile';
import LesterLaCombeProfile from './LesterLaCombeProfile';
import TiatoshiJamirProfile from './TiatoshiJamirProfile';
import AviholiChishiProfile from './AviholiChishiProfile';
import JyotiPatilProfile from './JyotiPatilProfile';
import EkRajOjhaProfile from './EkRajOjhaProfile';
import AdityaKiranKakatiProfile from './AdityaKiranKakatiProfile';
import AnnaNotsuProfile from './AnnaNotsuProfile';
import JelleWoutersProfile from './JelleWoutersProfile';
import JonathonKeatsProfile from './JonathonKeatsProfile';
import AniruddhaGuhaProfile from './AniruddhaGuhaProfile';

const CUSTOM_PROFILES = {
  'michael-heneise': MichaelHeneiseProfile,
  'lanuakum-aier': LanuakumAierProfile,
  'catriona-child': CatrionaChildProfile,
  'ruokuonuo-yhome': RuokuonuoYhomeProfile,
  'ruokuonuo-rose-yhome': RuokuonuoYhomeProfile,
  'rovithono-yhome': RovithonoYhomeProfile,
  'akumtong-imchen': AkumtongImchenProfile,
  'saktum-wonti': SaktumWontiProfile,
  'sholu-movi': ShoulMoviProfile,
  'khrieletuonuo-yhome': KhrieletuonuoYhomeProfile,
  'tumuzo-katiry': TumuzoKatiryProfile,
  'esther-swu': EstherSwuProfile,
  'kevide-lcho': KevideLchoProfile,
  'akumjung-pongen': AkumjungPongenProfile,
  'margaret-lyngdoh': MargaretLyngdohProfile,
  'lester-lacombe': LesterLaCombeProfile,
  'tiatoshi-jamir': TiatoshiJamirProfile,
  'aviholi-chishi': AviholiChishiProfile,
  'jyoti-patil': JyotiPatilProfile,
  'ek-raj-ojha': EkRajOjhaProfile,
  'aditya-kiran-kakati': AdityaKiranKakatiProfile,
  'anna-notsu': AnnaNotsuProfile,
  'jelle-j-p-wouters': JelleWoutersProfile,
  'jonathon-keats': JonathonKeatsProfile,
  'aniruddha-guha': AniruddhaGuhaProfile,
};

const PersonProfilePage = () => {
  const { id } = useParams();

  const CustomProfile = CUSTOM_PROFILES[id];
  if (CustomProfile) {
    return <CustomProfile />;
  }
  
  const person = getPersonById(id);

  // If person not found, redirect to people index
  if (!person) {
    return <Navigate to="/people" replace />;
  }

  const PlaceholderSection = ({ children }) => (
    <div className="text-center py-8 px-6 bg-stone-50 rounded border border-stone-200">
      <p className="text-stone-500 text-sm italic font-serif leading-relaxed">
        {children}
      </p>
    </div>
  );

  const SectionDivider = () => (
    <div className="my-12 flex items-center justify-center">
      <div className="h-px w-24 bg-stone-300"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb Navigation */}
      <div className="bg-stone-50 border-b border-stone-200">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center text-sm text-stone-600">
            <Link to="/" className="hover:text-green-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/people" className="hover:text-green-600 transition-colors">People</Link>
            <span className="mx-2">/</span>
            <span className="text-stone-900">{person.name}</span>
          </nav>
        </div>
      </div>

      {/* Identity Header */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-16 border-b border-stone-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              {/* Profile Image */}
              <div className="flex-shrink-0">
                <div className="w-48 h-48 rounded-lg overflow-hidden bg-stone-200 shadow-lg">
                  {person.image ? (
                    <img 
                      src={person.image} 
                      alt={person.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-20 h-20 text-stone-400" />
                    </div>
                  )}
                </div>
              </div>

              {/* Identity Information */}
              <div className="flex-1">
                <div className="mb-3">
                  <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-medium uppercase tracking-wider rounded-full">
                    {person.category}
                  </span>
                </div>
                <h1 className="text-4xl font-serif font-bold text-stone-900 mb-2">
                  {person.honorific && <span className="text-stone-600">{person.honorific} </span>}
                  {person.name}
                </h1>
                <p className="text-xl text-stone-700 font-medium mb-4">
                  {person.title}
                </p>
                {person.affiliation && (
                  <p className="text-stone-600 leading-relaxed">
                    {person.affiliation}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">

          {/* Intellectual Biography */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Intellectual Biography
            </h2>
            {person.bio ? (
              <div className="prose prose-stone max-w-none">
                <p className="text-stone-700 leading-relaxed text-lg">
                  {person.bio}
                </p>
              </div>
            ) : (
              <PlaceholderSection>
                Profile in development. This page will soon feature research interests, 
                scholarly trajectory, and intellectual contributions.
              </PlaceholderSection>
            )}
          </section>

          <SectionDivider />

          {/* Research Foci / Areas of Work */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Research Foci & Areas of Work
            </h2>
            <PlaceholderSection>
              Research themes and areas of scholarly inquiry will be detailed here, 
              reflecting long-term engagement with specific fields and questions.
            </PlaceholderSection>
          </section>

          <SectionDivider />

          {/* Highland Institute Projects */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Highland Institute Projects
            </h2>
            <PlaceholderSection>
              Current and past projects affiliated with the Highland Institute 
              will be documented here, demonstrating collaborative research commitments.
            </PlaceholderSection>
          </section>

          <SectionDivider />

          {/* Selected Publications & Knowledge Outputs */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Selected Publications & Knowledge Outputs
            </h2>
            <PlaceholderSection>
              A curated selection of scholarly publications, reports, and knowledge outputs 
              will be listed here, reflecting contributions to academic discourse.
            </PlaceholderSection>
          </section>

          <SectionDivider />

          {/* Public Engagement, Teaching & Community Work */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Public Engagement, Teaching & Community Work
            </h2>
            <PlaceholderSection>
              Details of teaching, public lectures, community collaborations, 
              and knowledge-sharing activities will be featured in this section.
            </PlaceholderSection>
          </section>

          <SectionDivider />

          {/* Contact & Scholarly Infrastructure */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Contact & Scholarly Infrastructure
            </h2>
            
            {/* If any contact info exists, show it; otherwise show placeholder */}
            {person.email || person.orcid || person.website ? (
              <div className="space-y-4">
                {person.email && (
                  <div className="flex items-center gap-3 text-stone-700">
                    <Mail className="w-5 h-5 text-green-600" />
                    <a 
                      href={`mailto:${person.email}`}
                      className="hover:text-green-600 transition-colors"
                    >
                      {person.email}
                    </a>
                  </div>
                )}
                
                {person.orcid && (
                  <div className="flex items-center gap-3 text-stone-700">
                    <ExternalLink className="w-5 h-5 text-green-600" />
                    <span className="font-medium mr-2">ORCID:</span>
                    <a 
                      href={`https://orcid.org/${person.orcid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-green-600 transition-colors"
                    >
                      {person.orcid}
                    </a>
                  </div>
                )}
                
                {person.website && (
                  <div className="flex items-center gap-3 text-stone-700">
                    <ExternalLink className="w-5 h-5 text-green-600" />
                    <a 
                      href={person.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-green-600 transition-colors"
                    >
                      Personal Website / Scholar Profile
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <PlaceholderSection>
                Contact information and scholarly profile links will be made available here, 
                facilitating academic correspondence and collaboration.
              </PlaceholderSection>
            )}
          </section>

        </div>
      </div>

      {/* Footer Note */}
      <div className="bg-stone-50 border-t border-stone-200 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-stone-500 font-serif italic">
              This profile is part of the Highland Institute's living archive of scholarly community. 
              Content will be expanded as collaborations and projects develop.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonProfilePage;
